// src/pages/produccion/UtilizacionCapacidad.jsx
import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import api from '../../services/api';

const META = 90; // % — según la ficha del KPI

const MESES = [
  ['01', 'Enero'], ['02', 'Febrero'], ['03', 'Marzo'], ['04', 'Abril'], ['05', 'Mayo'], ['06', 'Junio'],
  ['07', 'Julio'], ['08', 'Agosto'], ['09', 'Septiembre'], ['10', 'Octubre'], ['11', 'Noviembre'], ['12', 'Diciembre'],
];
function ultimoDiaDelMes(anio, mes) {
  return new Date(anio, parseInt(mes), 0).getDate();
}
function primerDiaMesActual() {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function UtilizacionCapacidad() {
  const [registros, setRegistros] = useState([]);
  const [periodo, setPeriodo] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [fDesde, setFDesde] = useState(primerDiaMesActual());
  const [fHasta, setFHasta] = useState(new Date().toISOString().slice(0, 10));
  const [fMes, setFMes] = useState('');
  const [fAnio, setFAnio] = useState(new Date().getFullYear());

  async function cargar() {
    try {
      setCargando(true);
      const params = {};
      if (fDesde) params.desde = fDesde;
      if (fHasta) params.hasta = fHasta;
      const res = await api.get('/api/produccion/capacidad-resumen', { params });
      setRegistros(res.data.data);
      setPeriodo(res.data.periodo);
      setError(null);
    } catch (err) {
      console.error(err);
      setError('No se pudo cargar el resumen.');
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleMesChange(mes) {
    setFMes(mes);
    if (mes) {
      setFDesde(`${fAnio}-${mes}-01`);
      setFHasta(`${fAnio}-${mes}-${String(ultimoDiaDelMes(fAnio, mes)).padStart(2, '0')}`);
    }
  }

  function exportarExcel() {
    const filas = registros.map((r) => ({
      Línea: r.linea,
      'Capacidad Máx. Semanal (kg)': r.capacidad_kg_semana,
      'Capacidad del Período (kg)': r.capacidad_periodo.toFixed(2),
      'Producción Real (kg)': r.producido.toFixed(2),
      '% Utilización': r.pct_utilizacion.toFixed(2),
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    ws['!cols'] = [{ wch: 10 }, { wch: 20 }, { wch: 20 }, { wch: 18 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Utilización Capacidad');
    XLSX.writeFile(wb, `dasavena_utilizacion_capacidad_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  return (
    <div style={styles.paginaContenedor}>
      <div style={styles.headerFila}>
        <div>
          <h1 style={styles.tituloPagina}>Utilización de Capacidad</h1>
          <p style={styles.subtituloPagina}>% Utilización = Producción Real (Mezclado) ÷ Capacidad Máxima del período · Meta: &gt;{META}%</p>
        </div>
        <button onClick={exportarExcel} disabled={registros.length === 0} style={styles.botonExcel}>⬇ Descargar Excel</button>
      </div>

      <div style={styles.filtrosBar}>
        <FiltroFecha label="Desde" value={fDesde} onChange={(v) => { setFDesde(v); setFMes(''); }} />
        <FiltroFecha label="Hasta" value={fHasta} onChange={(v) => { setFHasta(v); setFMes(''); }} />
        <FiltroSel label="Mes" value={fMes} onChange={handleMesChange} options={[['', '—'], ...MESES]} />
        <div style={styles.filtroGrupo}>
          <label style={styles.filtroLabel}>Año</label>
          <input type="number" value={fAnio} onChange={(e) => setFAnio(e.target.value)} style={{ ...styles.filtroInput, width: '80px' }} />
        </div>
        <button onClick={cargar} style={styles.botonBuscar2}>Buscar</button>
      </div>

      {periodo && (
        <p style={styles.notaPeriodo}>
          Período: {new Date(periodo.desde).toLocaleDateString('es-MX')} al {new Date(periodo.hasta).toLocaleDateString('es-MX')}
          {' '}({periodo.dias} días ≈ {periodo.semanas.toFixed(2)} semanas)
        </p>
      )}

      {cargando ? <div>Cargando...</div> : error ? <div style={{ color: '#B23A48' }}>{error}</div> : (
        <div style={styles.tarjetasLineas}>
          {registros.map((r) => {
            const semaforo = r.pct_utilizacion >= META ? '#3E7C5A' : r.pct_utilizacion >= META - 15 ? '#C08A2B' : '#B23A48';
            const anchoBarra = Math.min(r.pct_utilizacion, 100);
            return (
              <div key={r.linea} style={{ ...styles.lineaCard, borderTop: `3px solid ${semaforo}` }}>
                <div style={styles.lineaTitulo}>{r.linea}</div>
                <div style={{ ...styles.lineaPct, color: semaforo }}>{r.pct_utilizacion.toFixed(1)}%</div>
                <div style={styles.barraFondo}>
                  <div style={{ ...styles.barraRelleno, width: `${anchoBarra}%`, background: semaforo }} />
                </div>
                <div style={styles.lineaDetalle}>
                  <span>{r.producido.toFixed(0)} kg producidos</span>
                  <span>de {r.capacidad_periodo.toFixed(0)} kg capacidad</span>
                </div>
                <div style={styles.lineaSub}>Capacidad máxima: {r.capacidad_kg_semana.toLocaleString('es-MX')} kg/semana</div>
              </div>
            );
          })}
          {registros.length === 0 && <p style={{ color: '#8A7690', fontSize: 12 }}>Sin datos para este período.</p>}
        </div>
      )}
    </div>
  );
}

function FiltroSel({ label, value, onChange, options }) {
  return (
    <div style={styles.filtroGrupo}>
      <label style={styles.filtroLabel}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={styles.filtroInput}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
function FiltroFecha({ label, value, onChange }) {
  return (
    <div style={styles.filtroGrupo}>
      <label style={styles.filtroLabel}>{label}</label>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)} style={styles.filtroInput} />
    </div>
  );
}

const styles = {
  paginaContenedor: { fontFamily: 'Inter, sans-serif', padding: '24px', color: '#3A2E42' },
  headerFila: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '14px', marginBottom: '4px' },
  tituloPagina: { fontFamily: 'Playfair Display, serif', color: '#2A1830', marginBottom: '2px' },
  subtituloPagina: { fontSize: '12px', color: '#8A7690' },
  botonExcel: { padding: '8px 16px', fontSize: '12px', fontWeight: 600, color: '#fff', background: '#3E7C5A', border: 'none', borderRadius: '8px', cursor: 'pointer', flexShrink: 0 },
  filtrosBar: { display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', background: '#fff', border: '1px solid #E6DDD2', borderRadius: '10px', padding: '14px 16px', marginTop: '14px', marginBottom: '6px' },
  filtroGrupo: { display: 'flex', flexDirection: 'column', gap: '4px' },
  filtroLabel: { fontSize: '10px', color: '#8A7690', textTransform: 'uppercase', letterSpacing: '0.4px' },
  filtroInput: { padding: '6px 8px', fontSize: '12px', border: '1px solid #E6DDD2', borderRadius: '6px', fontFamily: 'Inter, sans-serif' },
  botonBuscar2: { padding: '7px 16px', fontSize: '12px', fontWeight: 600, color: '#fff', background: '#4A1E5C', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  notaPeriodo: { fontSize: '11px', color: '#8A7690', marginBottom: '18px' },
  tarjetasLineas: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' },
  lineaCard: { background: '#fff', border: '1px solid #E6DDD2', borderRadius: '14px', padding: '20px', boxShadow: '0 2px 14px rgba(74,30,92,0.07)' },
  lineaTitulo: { fontSize: '13px', fontWeight: 700, color: '#6E4F80', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' },
  lineaPct: { fontSize: '34px', fontWeight: 700, marginBottom: '10px' },
  barraFondo: { width: '100%', height: '10px', background: '#F0E9E0', borderRadius: '6px', overflow: 'hidden', marginBottom: '10px' },
  barraRelleno: { height: '100%', borderRadius: '6px', transition: 'width 0.3s ease' },
  lineaDetalle: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#3A2E42', marginBottom: '6px' },
  lineaSub: { fontSize: '11px', color: '#8A7690' },
};